/* ---------------------------------------------------------- */
/* Container                                               */
/* ---------------------------------------------------------- */

.PricingOptions {
  --brand-pricing-options-item-gap: var(--brand-PricingOptions-items-gap);
  --brand-pricing-options-container-padding-inline: 0;
  --brand-pricing-options-column-count: 1;

  /* prettier-ignore */
  --brand-pricing-options-template-areas: 
    'labels'
    'leadingComponent'
    'header'
    'description'
    'price'
    'actions'
    'feature-list'
    'footnote'
    ;

  display: grid;
  row-gap: var(--brand-pricing-options-item-gap, 0);
  padding-inline: var(--brand-pricing-options-container-padding-inline);
}

@media screen and (max-width: 63.24rem) {
  .PricingOptions {
    margin-inline: auto;
    max-width: 488px;
  }
}

@media (min-width: 63.25rem) {
  .PricingOptions {
    grid-template-areas: var(--brand-pricing-options-template-areas);
    grid-template-columns: repeat(var(--brand-pricing-options-column-count), minmax(0, 1fr));
    column-gap: var(--brand-pricing-options-item-gap, 0);
    row-gap: 0;
  }

  .PricingOptions--items1 {
    --brand-pricing-options-column-count: 1;
  }

  .PricingOptions--items2 {
    --brand-pricing-options-column-count: 2;
  }

  .PricingOptions--items3 {
    --brand-pricing-options-column-count: 3;
  }

  .PricingOptions--items4 {
    --brand-pricing-options-column-count: 4;
  }
}

.PricingOptions__labels {
  display: none;
}

@media (min-width: 63.25rem) {
  .PricingOptions__labels {
    --brand-pricing-options-column-count: 1;

    display: grid;
    grid-column: 1 / -1;
    grid-row: labels;
    grid-template-columns: repeat(var(--brand-pricing-options-column-count), minmax(0, 1fr));
    column-gap: var(--brand-pricing-options-item-gap, 0);
  }

  .PricingOptions__label-cell {
    position: relative;
    display: flex;
    align-items: center;
    min-height: var(--base-size-54);
    padding-block: var(--base-size-16);
    background-color: var(--brand-PricingOptions-item-bgColor-solid);
  }

  /* Accent top border — uses ::after so it can overflow into the column gap */
  .PricingOptions__label-cell--has-label::after {
    content: '';
    position: absolute;
    inset-block-start: 0;
    inset-inline-start: calc(var(--brand-pricing-options-item-gap, 0px) / 2 * -1);
    inset-inline-end: calc(var(--brand-pricing-options-item-gap, 0px) / 2 * -1);
    height: var(--brand-borderWidth-thin);
    background-color: var(--brand-color-text-emphasized);
    z-index: 1;
  }

  /* First cell: don't overflow past container start (cards override below) */
  .PricingOptions__label-cell--has-label:first-child::after {
    inset-inline-start: 0;
  }

  /* Last cell: don't overflow past container end (cards override below) */
  .PricingOptions__label-cell--has-label:last-child::after {
    inset-inline-end: 0;
  }

  .PricingOptions--layout-default .PricingOptions__label-cell--has-label::after {
    inset-inline-start: 0;
    inset-inline-end: 0;
  }

  /* ---- Default layout labels ---- */

  /* Continuous bottom border across entire labels row — uses ::after to extend into container padding */
  .PricingOptions--layout-default .PricingOptions__labels {
    position: relative;
  }

  .PricingOptions--layout-default .PricingOptions__labels::after {
    content: '';
    position: absolute;
    inset-block-end: 0;
    inset-inline-start: calc(var(--brand-pricing-options-container-padding-inline, 0px) * -1);
    inset-inline-end: calc(var(--brand-pricing-options-container-padding-inline, 0px) * -1);
    height: var(--brand-borderWidth-thin);
    background-color: var(--brand-color-border-subtle);
  }

  .PricingOptions--layout-default .PricingOptions__label-cell {
    margin-inline: calc(var(--brand-pricing-options-item-gap) / 2 * -1);
    padding-inline: calc(var(--brand-pricing-options-item-gap) / 2);
  }

  .PricingOptions--layout-default .PricingOptions__label-cell:first-child {
    margin-inline-start: calc(var(--brand-pricing-options-container-padding-inline) * -1);
    padding-inline-start: var(--brand-pricing-options-container-padding-inline);
  }

  .PricingOptions--layout-default .PricingOptions__label-cell:last-child {
    margin-inline-end: calc(var(--brand-pricing-options-container-padding-inline) * -1);
    padding-inline-end: var(--brand-pricing-options-container-padding-inline);
  }

  /* Empty cells: transparent bg/borders but structurally visible so dividers show */
  .PricingOptions--layout-default .PricingOptions__label-cell--empty {
    background-color: transparent;
    border-block-start-color: transparent;
  }

  /* Vertical dividers between label cells, centered in the gap */
  .PricingOptions--layout-default .PricingOptions__label-cell + .PricingOptions__label-cell::before {
    content: '';
    position: absolute;
    inset-block: 0;
    width: var(--brand-borderWidth-thin);
    inset-inline-start: 0;
    background-color: var(--brand-color-border-subtle);
  }

  .PricingOptions--layout-default.PricingOptions--appearance-gradient
    .PricingOptions__label-cell
    + .PricingOptions__label-cell::before {
    mask-image: var(--brand-PricingOptions-borderMask);
  }

  /* ---- Cards layout labels ---- */

  .PricingOptions--layout-cards .PricingOptions__label-cell {
    padding-inline: var(--base-size-28);
    border-inline: var(--brand-borderWidth-thin) solid var(--brand-color-border-subtle);
    /* border-block-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted); */
  }

  .PricingOptions--layout-cards .PricingOptions__label-cell--empty {
    opacity: 0;
    pointer-events: none;
  }

  /* Cards: keep accent border inside the card boundary */
  .PricingOptions--layout-cards .PricingOptions__label-cell--has-label::after {
    inset-inline-start: 0;
    inset-inline-end: 0;
  }
}

/* ---------------------------------------------------------- */
/* Layout: default                                         */
/* ---------------------------------------------------------- */

.PricingOptions--layout-default {
  position: relative;
}

.PricingOptions--layout-default.PricingOptions--appearance-solid {
  border: solid 1px var(--brand-color-border-subtle);
  background-color: var(--brand-PricingOptions-item-bgColor-solid);
}

.PricingOptions--layout-default.PricingOptions--appearance-gradient,
.PricingOptions--layout-cards.PricingOptions--appearance-gradient .PricingOptions__item {
  background: linear-gradient(
    180deg,
    var(--brand-PricingOptions-item-bgColor-gradient-start) 0%,
    var(--brand-PricingOptions-item-bgColor-gradient-end) 100%
  );
}

.PricingOptions--layout-default.PricingOptions--appearance-gradient::before,
.PricingOptions--layout-cards.PricingOptions--appearance-gradient .PricingOptions__item::before {
  position: absolute;
  content: '';
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  mask-image: var(--brand-PricingOptions-borderMask);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-subtle);
}

@media (min-width: 63.25rem) {
  .PricingOptions--layout-default {
    padding-inline: var(--brand-pricing-options-container-padding-inline);
  }
}

.PricingOptions--layout-default .PricingOptions__item {
  --brand-pricing-options-item-padding-inline: var(--base-size-24);

  position: relative; /* Used for positioning the divider between items */
  padding-inline: var(--base-size-24);
  padding-block: var(--base-size-20);
}

@media (min-width: 63.25rem) {
  .PricingOptions--layout-default .PricingOptions__item {
    --brand-pricing-options-item-padding-inline: 0;

    padding-inline: 0;
    padding-block: var(--base-size-40);
  }
}

/* Horizontal divider between items on small viewports */
.PricingOptions--layout-default .PricingOptions__item + .PricingOptions__item::before {
  content: '';
  position: absolute;
  inset-inline: 0;
  height: 1px;
  background-color: var(--brand-color-border-subtle);
}

/* Vertical divider between items on larger viewports */
@media (min-width: 63.25rem) {
  .PricingOptions--layout-default .PricingOptions__item + .PricingOptions__item::before {
    inset-inline: 0;
    inset-block: 0;
    width: 1px;
    height: auto;
    margin-block-start: 0;
    margin-inline-start: calc(var(--brand-pricing-options-item-gap) / 2 * -1);
  }

  .PricingOptions--layout-default.PricingOptions--appearance-gradient
    .PricingOptions__item
    + .PricingOptions__item::before {
    mask-image: var(--brand-PricingOptions-borderMask);
  }
}

/* ---------------------------------------------------------- */
/* Layout: cards                                           */
/* ---------------------------------------------------------- */

.PricingOptions--layout-cards .PricingOptions__item {
  --brand-pricing-options-item-padding-inline: var(--base-size-32);

  position: relative;
  padding-inline: var(--base-size-32);
  padding-block: var(--base-size-32);
}

.PricingOptions--layout-cards.PricingOptions--appearance-solid .PricingOptions__item {
  background-color: var(--brand-PricingOptions-item-bgColor-solid);
  border: solid var(--brand-borderWidth-thin) var(--brand-color-border-subtle);
}

/* ---------------------------------------------------------- */
/* Items                                                      */
/* ---------------------------------------------------------- */

@media (min-width: 48rem) {
  :where(.PricingOptions--layout-cards.PricingOptions--items4) {
    row-gap: var(--brand-pricing-options-item-gap);
  }
}

@media (min-width: 63.25rem) {
  .PricingOptions--items1 {
    width: 50%;
    margin-inline: auto;
  }

  .PricingOptions--layout-default.PricingOptions--items1 {
    --brand-pricing-options-container-padding-inline: var(--brand-PricingOptions-items1-container-paddingInline);
  }

  .PricingOptions--layout-default.PricingOptions--items2 {
    --brand-pricing-options-item-gap: var(--brand-PricingOptions-items2-gap);
    --brand-pricing-options-container-padding-inline: var(--brand-PricingOptions-items2-container-padding-inline);
  }

  .PricingOptions--layout-default.PricingOptions--items3 {
    --brand-pricing-options-item-gap: var(--brand-PricingOptions-items3-gap);
    --brand-pricing-options-container-padding-inline: var(--brand-PricingOptions-items3-container-padding-inline);
  }
  .PricingOptions--layout-default.PricingOptions--items4 {
    --brand-pricing-options-item-gap: var(--brand-PricingOptions-items4-gap);
    --brand-pricing-options-container-padding-inline: var(--brand-PricingOptions-items4-container-padding-inline);
  }
}

/* ---------------------------------------------------------- */
/* Item inner layout                                       */
/* ---------------------------------------------------------- */

.PricingOptions__item {
  display: grid;
  grid-template-areas: var(--brand-pricing-options-template-areas);
}

@media (min-width: 63.25rem) {
  .PricingOptions__item {
    grid-row: header / footnote;
    grid-template-rows: subgrid;
  }

  .PricingOptions__item--has-leading-component {
    grid-row: leadingComponent / footnote;
  }
}

.PricingOptions__item--align-center {
  justify-items: center;
}

.PricingOptions__item--align-center .PricingOptions__price,
.PricingOptions__item--align-center .PricingOptions__description {
  text-align: center;
  justify-content: center;
}

/*
 * Backwards compatibility for browsers that don't support subgrid.
 * Once subgrid is supported in the last 3 major versions of Safari (i.e., when Safari 18 is released), it can be fully removed.
 * https://caniuse.com/css-subgrid
 */
@supports not (grid-template-rows: subgrid) {
  @media (min-width: 63.28rem) {
    .PricingOptions__item {
      grid-template-areas: var(--brand-pricing-options-template-areas);
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    }
  }
}

/* ---------------------------------------------------------- */
/* Content                                                 */
/* ---------------------------------------------------------- */

.PricingOptions__header {
  display: flex;
  grid-area: header;
  row-gap: var(--base-size-16);
}

/* When item is left-aligned, keep the heading at the start of the card */
.PricingOptions__item:not(.PricingOptions__item--align-center) .PricingOptions__header {
  align-items: center;
  margin-block-end: var(--base-size-16);
}

.PricingOptions__item:not(.PricingOptions__item--align-center) .PricingOptions__heading {
  margin-block: var(--base-size-16);
  flex: 0 1 auto;
  order: 1;
  align-self: flex-start;
}

@media (min-width: 63.24rem) {
  .PricingOptions__item:not(.PricingOptions__item--align-center) .PricingOptions__heading {
    margin-block: 0;
  }
}

/* When item is centered, center the heading */
.PricingOptions__item--align-center .PricingOptions__header {
  justify-content: center;
}

.PricingOptions__item--align-center .PricingOptions__heading {
  margin-block-start: 0;
  margin-block-end: var(--base-size-16);
  text-align: center;
}

.PricingOptions__leading-component {
  grid-row: leadingComponent;
}

.PricingOptions__leading-component :is(img, picture) {
  display: block;
}

.PricingOptions__label {
  color: var(--brand-color-text-default);
  font-family: var(--brand-fontStack-monospace);
  font-size: var(--brand-text-size-100);
  font-weight: var(--brand-text-weight-500);
  letter-spacing: var(--brand-text-letterSpacing-100);
  line-height: var(--brand-text-lineHeight-200);
  text-transform: uppercase;
}

.PricingOptions__heading {
  margin-block: var(--base-size-16);
  flex: 0 1 auto; /* Minimum width before wrapping */
  order: 1;
  align-self: flex-start;
}

.PricingOptions__description {
  grid-row: description;
}

.PricingOptions__price {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  grid-row: price;
  padding-block-start: var(--base-size-20);
  padding-block-end: var(--base-size-8);
}

@media (max-width: 63.25rem) {
  .PricingOptions__price-value,
  .PricingOptions__price-currency-symbol {
    font-size: var(--brand-text-size-900);
  }
}

.PricingOptions__price-amount {
  display: flex;
  align-items: flex-end;
}

.PricingOptions__price-currency-code {
  margin-inline-start: var(--base-size-4);
  margin-block-end: var(--base-size-4);
  color: var(--brand-color-text-muted);
}

.PricingOptions__price-original-price {
  display: inline-flex;
  align-self: center;
  text-decoration: none;
  margin-inline-start: var(--base-size-8);
}

.PricingOptions__price-original-price-value {
  text-decoration: line-through;
}

.PricingOptions__price-trailing-text {
  flex: 1 0 100%;
  display: block;
}

.PricingOptions__actions {
  grid-row: actions;
  display: flex;
  flex-direction: column;
  gap: var(--base-size-8);
  align-items: flex-end;
  padding-block-start: var(--base-size-20);
}

.PricingOptions__actions-message {
  display: flex;
  gap: var(--base-size-12);
  align-items: flex-start;
  inline-size: 100%;
  max-inline-size: 100%;
  padding: var(--base-size-12);
  border-block: var(--brand-borderWidth-thin) solid var(--brand-PricingOptions-actionsMessage-borderColor);
  border-inline: 0;
  border-radius: 0;
  background-color: var(--brand-PricingOptions-actionsMessage-bgColor);
}

.PricingOptions__actions-message-leading-component {
  flex: 0 0 auto;
  display: flex;
  margin-block-start: var(--base-size-4);
  color: var(--brand-PricingOptions-actionsMessage-accentColor);
}

.PricingOptions__actions-message-leading-component :where(svg) {
  display: block;
  inline-size: var(--base-size-16);
  block-size: var(--base-size-16);
}

.PricingOptions__actions-message-content {
  flex: 1 1 auto;
  min-inline-size: 0;
}

.PricingOptions__actions-message-content :where(p) {
  margin: 0;
}

/* MenuAction: full-width ActionMenu wrapper */
.PricingOptions__menu-action {
  width: 100%;
}

.PricingOptions__menu-action > * {
  width: 100%;
}

/* Force ActionMenu button container to stretch full width */
.PricingOptions__menu-action > * > * {
  display: flex;
  width: 100%;
}

/* Make the primary button fill remaining space next to the chevron */
.PricingOptions__menu-action > * > * > :first-child {
  flex: 1;
}

.PricingOptions__footnote {
  grid-row: footnote;
  display: block;
  margin-block-start: var(--base-size-8);
}

/* ---------------------------------------------------------- */
/* Feature list                                            */
/* ---------------------------------------------------------- */

.PricingOptions__feature-list {
  --feature-list-font-size: var(--brand-text-size-200);
  --feature-list-line-height: var(--brand-text-lineHeight-200);

  display: flex;
  flex-direction: column;
  gap: var(--base-size-8);
  grid-row: feature-list;
  margin-block-start: var(--base-size-20);
  padding-block-start: var(--base-size-20);
  width: 100%;
}

.PricingOptions__feature-list--has-divider {
  position: relative;
  margin: 0;
  margin-block-start: var(--base-size-20);
  padding-block-start: var(--base-size-40);
  padding-inline: 0;
}

/* Edge-to-edge divider above feature list */
.PricingOptions__feature-list--has-divider::before {
  content: '';
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: calc(var(--brand-pricing-options-item-padding-inline, 0px) * -1);
  inset-inline-end: calc(var(--brand-pricing-options-item-padding-inline, 0px) * -1);
  height: var(--brand-borderWidth-thin);
  background-color: var(--brand-color-border-subtle);
}

/* Default layout: extend feature list divider into column gap */
@media (min-width: 63.25rem) {
  .PricingOptions--layout-default .PricingOptions__feature-list--has-divider::before {
    inset-inline-start: calc(var(--brand-pricing-options-item-gap, 0px) / 2 * -1);
    inset-inline-end: calc(var(--brand-pricing-options-item-gap, 0px) / 2 * -1);
  }

  /* First item: don't overflow past container start */
  .PricingOptions--layout-default .PricingOptions__item:first-child .PricingOptions__feature-list--has-divider::before,
  .PricingOptions--layout-default
    .PricingOptions__labels
    + .PricingOptions__item
    .PricingOptions__feature-list--has-divider::before {
    inset-inline-start: calc(var(--brand-pricing-options-container-padding-inline, 0px) * -1);
  }

  /* Last item: don't overflow past container end */
  .PricingOptions--layout-default .PricingOptions__item:last-child .PricingOptions__feature-list--has-divider::before {
    inset-inline-end: calc(var(--brand-pricing-options-container-padding-inline, 0px) * -1);
  }

  .PricingOptions--layout-default.PricingOptions--appearance-gradient
    .PricingOptions__feature-list--has-divider::before {
    mask-image: var(--brand-PricingOptions-borderMask);
  }
}

.PricingOptions__feature-list-set:nth-child(n + 2) {
  margin-block-start: var(--base-size-20);
}

.PricingOptions__feature-list-group-heading {
  color: var(--brand-PricingOptions-featureList-group-heading-color);
  margin-block-start: var(--base-size-12);
  margin-block-end: var(--base-size-8);
}

.PricingOptions__feature-list-item--excluded span {
  color: var(--brand-color-text-muted);
}

/* Info tooltip button */
.PricingOptions__feature-list-item--has-info {
  flex-wrap: nowrap;
}

.PricingOptions__feature-list-item--has-info > span {
  flex: 1;
  display: inline-flex;
  align-items: baseline;
  justify-content: space-between;
}

.PricingOptions__feature-list-item-info {
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  color: var(--brand-color-text-muted);
  background-color: color-mix(in srgb, var(--brand-color-canvas-invert) 12%, transparent);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  border-radius: 50%;
  margin-inline-start: var(--base-size-8);
  vertical-align: middle;
  padding: var(--base-size-4);
}

.PricingOptions__feature-list-item-info svg {
  width: var(--base-size-12);
  height: var(--base-size-12);
}

.PricingOptions__feature-list-item-info:focus-visible {
  outline: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
  outline-offset: 2px;
}

.PricingOptions__feature-list + .PricingOptions__feature-list-group-heading {
  margin-block-start: var(--base-size-20);
}

.PricingOptions__feature-list-item,
.PricingOptions__feature-list-item span {
  font-size: var(--feature-list-font-size) !important;
  line-height: var(--feature-list-line-height) !important;
}

.PricingOptions__feature-list-group-heading {
  font-size: var(--feature-list-font-size) !important;
}

.PricingOptions--items4 .PricingOptions__feature-list {
  --feature-list-font-size: var(--brand-text-size-100);
  --feature-list-line-height: var(--brand-text-lineHeight-100);
}

/*
 * Accordion component overrides
 */

/*  paddings and borders */
.PricingOptions__feature-list-accordion {
  border: 0;
  padding: 0;
}

/* Remove hover effect */
.PricingOptions__feature-list-accordion::after {
  content: none !important;
}

/* Adjust heading spacing */
.PricingOptions__feature-list-accordion-heading {
  padding: var(--base-size-8) 0;
  padding-inline-start: 0 !important; /* Fixes dotcom specificity issue */
  padding-inline-end: 0 !important;
  margin-block-start: calc(var(--base-size-16) * -1);
  margin-block-end: var(--base-size-8);
}

/* Accordion heading styling */
.PricingOptions__feature-list-accordion .PricingOptions__feature-list-accordion-heading :is(h1, h2, h3, h4, h5, h6) {
  font-size: var(--feature-list-font-size);
  line-height: var(--feature-list-line-height);
  font-weight: var(--brand-text-subhead-weight-large);
  gap: var(--base-size-8);
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;
}

/* Hide default icon */
.PricingOptions__feature-list-accordion .PricingOptions__feature-list-accordion-heading span {
  display: none;
}

/* Adjust content paddings */
.PricingOptions__feature-list-accordion .PricingOptions__feature-list-accordion-content {
  padding: 0;
  padding-block-start: var(--base-size-8);
  padding-inline-start: 0 !important;
}

.PricingOptions__feature-list-accordion-chevron {
  margin-inline-end: var(--base-size-4);
}

/* Chevron up icon */
.PricingOptions__feature-list-accordion[open] .PricingOptions__feature-list-accordion-chevron {
  transform: rotate(180deg);
}

.PricingOptions__feature-list-accordion-content {
  animation: none !important;
}
